<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3';
import { Sparkles } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import RunProgressTracker from '@/components/shared/RunProgressTracker.vue';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import * as runsRoutes from '@/routes/runs';
import type { Project } from '@/types';

interface SearchEquation {
    id: string;
    database_name: string;
    equation_text: string;
    result_count: number | null;
    created_at: string;
}

interface Props {
    project: Project;
    equations: SearchEquation[];
    active_run_id?: string | null;
}

const props = defineProps<Props>();
const toast = useToast();

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
                { title: 'Équations de recherche', href: '#' },
            ],
        },
    ],
});

// ─── Génération IA des équations (run search_equation) ──────────────────────────

const generating = ref(false);
const runId = ref<string | null>(null);

onMounted(() => {
    if (props.active_run_id) {
        runId.value = props.active_run_id;
    }
});

async function generateEquations() {
    if (generating.value || runId.value) {
        return;
    }

    generating.value = true;

    try {
        const csrfToken =
            (
                document.querySelector(
                    'meta[name="csrf-token"]',
                ) as HTMLMetaElement
            )?.content ?? '';
        const res = await fetch(runsRoutes.store().url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                Accept: 'application/json',
            },
            body: JSON.stringify({
                project_id: props.project.id,
                type: 'search_equation',
            }),
        });

        if (res.status === 409) {
            const data = (await res.json().catch(() => ({}))) as {
                active_run_id?: string;
            };
            toast.info('Une génération est déjà en cours pour ce projet.');

            if (data.active_run_id) {
                runId.value = data.active_run_id;
            }

            return;
        }

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(
                (err as { message?: string }).message ??
                    'Erreur lors du lancement de la génération.',
            );

            return;
        }

        const run = (await res.json()) as { id: string };
        runId.value = run.id;
        toast.success('Génération des équations lancée…');
    } catch {
        toast.error('Erreur réseau lors du lancement de la génération.');
    } finally {
        generating.value = false;
    }
}

function onGenerationCompleted() {
    router.reload({ only: ['equations'] });
    toast.success('Équations générées.');
    setTimeout(() => {
        runId.value = null;
    }, 1500);
}

function onGenerationFailed() {
    toast.error('La génération des équations a échoué.');
    runId.value = null;
}

function onGenerationCancelled() {
    runId.value = null;
    router.reload({ only: ['equations'] });
}

async function copyEquation(text: string) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success('Équation copiée.');
    } catch {
        toast.error("Impossible de copier l'équation.");
    }
}
</script>

<template>
    <Head :title="`Équations de recherche — ${project.name}`" />

    <div class="flex-1 overflow-y-auto" data-tour="search-equations">
        <div class="mx-auto max-w-3xl px-10 py-12">
            <!-- Page Header -->
            <header class="mb-8 flex items-start justify-between">
                <div>
                    <div
                        class="mb-1 text-xs font-semibold tracking-widest text-muted-foreground uppercase"
                    >
                        Préparation
                    </div>
                    <h1
                        class="text-2xl font-bold tracking-tight text-foreground"
                    >
                        Équations de recherche
                    </h1>
                    <p class="mt-1 text-sm text-muted-foreground">
                        Une équation par base de données — l'assistant les
                        rédige à partir de votre configuration, vous les ajustez
                        avant interrogation.
                    </p>
                </div>
                <div class="shrink-0 pl-4">
                    <button
                        type="button"
                        :disabled="generating || !!runId"
                        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                        data-tour="generate-equations-btn"
                        @click="generateEquations"
                    >
                        <Sparkles class="h-3.5 w-3.5" />
                        {{
                            generating
                                ? 'Lancement…'
                                : runId
                                  ? 'Génération en cours…'
                                  : 'Proposer les équations'
                        }}
                    </button>
                </div>
            </header>

            <!-- Suivi de génération IA -->
            <RunProgressTracker
                v-if="runId"
                :run-id="runId"
                feature-label="Génération des équations…"
                class="mb-6 rounded-lg border"
                @completed="onGenerationCompleted"
                @failed="onGenerationFailed"
                @cancelled="onGenerationCancelled"
            />

            <!-- Empty state -->
            <div
                v-if="equations.length === 0"
                class="rounded-lg border border-dashed border-border p-8 text-center"
            >
                <p class="text-sm text-muted-foreground">
                    Aucune équation de recherche générée pour ce projet.
                </p>
            </div>

            <!-- Equation blocks -->
            <div v-else data-tour="equations-result">
                <div
                    v-for="eq in equations"
                    :key="eq.id"
                    class="mb-4 overflow-hidden rounded-lg border border-border"
                >
                    <!-- Header -->
                    <div
                        class="flex items-center justify-between bg-muted/50 px-4 py-3"
                    >
                        <div class="flex items-center gap-3">
                            <span
                                class="rounded bg-foreground/10 px-2 py-0.5 text-xs font-semibold tracking-wide uppercase"
                            >
                                {{ eq.database_name }}
                            </span>
                            <span class="text-xs text-muted-foreground"
                                >Mise à jour
                                {{
                                    new Date(eq.created_at).toLocaleDateString(
                                        'fr-FR',
                                    )
                                }}</span
                            >
                        </div>
                        <div class="flex items-center gap-2">
                            <span
                                class="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
                            >
                                ✦ IA
                            </span>
                            <button
                                class="rounded border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                            >
                                Tester
                            </button>
                            <button
                                class="rounded border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                            >
                                Lancer
                            </button>
                        </div>
                    </div>

                    <!-- Equation text -->
                    <div
                        class="bg-background px-4 py-3 font-mono text-sm leading-relaxed"
                    >
                        {{ eq.equation_text }}
                    </div>

                    <!-- Footer -->
                    <div
                        class="flex items-center justify-between border-t border-border/50 bg-muted/20 px-4 py-2"
                    >
                        <span
                            v-if="eq.result_count !== null"
                            class="text-xs text-muted-foreground"
                        >
                            ~{{ eq.result_count }} résultats
                        </span>
                        <span v-else class="text-xs text-muted-foreground"
                            >Résultats inconnus</span
                        >
                        <div class="flex items-center gap-3">
                            <a
                                class="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                >Modifier</a
                            >
                            <button
                                type="button"
                                class="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                @click="copyEquation(eq.equation_text)"
                            >
                                Copier
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Form footer -->
                <div class="mt-2 flex items-center justify-between">
                    <a
                        class="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                        >+ Ajouter une équation</a
                    >
                    <button
                        class="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
                    >
                        Lancer toutes
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
